import React, { useMemo } from "react";
import { PublishedCard } from "../../types";
import CardPreviewModal from "./CardPreviewModal";

interface PublishedCardPreviewModalProps {
  open: boolean;
  onClose: () => void;
  card: PublishedCard | null;
  additionalPages?: {
    order: number;
    type: string;
    content: any;
    assets: number[];
  }[];
  /** Custom header actions to render next to the close button in the preview modal. */
  headerRightSlot?: React.ReactNode;
  showCoverPage?: boolean;
  /** Collect / purchase this Card. Drives the Impact Card poster CTA. */
  onCollect?: () => void;
}

const PublishedCardPreviewModal: React.FC<PublishedCardPreviewModalProps> = ({
  open,
  onClose,
  card,
  additionalPages,
  headerRightSlot,
  showCoverPage = false,
  onCollect,
}) => {
  if (!card) return null;

  // Convert page type from API format (with underscores) to preview modal format (without underscores)
  const convertPageType = (type: string): string => {
    // Map types with underscores to types without underscores
    const typeMap: Record<string, string> = {
      Single_Card: "SingleCard",
      Season_Card: "SeasonCard",
      season_card: "SeasonCard",
      Demo_Card: "DemoCard",
      demo_card: "DemoCard",
      Collection_Card: "CollectionCard",
      collection_card: "CollectionCard",
      Impact_Card: "Impact_Card",
      impact_card: "Impact_Card",
      // Types without underscores remain unchanged
      Image: "Image",
      Text: "Text",
      Audio: "Audio",
      Video: "Video",
      Youtube: "Youtube",
      News: "News",
      CoverImage: "CoverImage",
    };
    return typeMap[type] || type;
  };

  const transformPage = (
    page: PublishedCard["pages"][number] | Record<string, unknown>
  ): {
    id?: number;
    order: number;
    type: string;
    content: any;
    assets: number[];
    creator?: unknown;
    status?: unknown;
    parent_id?: unknown;
    parent_page_id?: unknown;
    sub_pages?: unknown[];
  } => {
    const raw = page as Record<string, unknown>;
    const nested = Array.isArray(raw.sub_pages) ? raw.sub_pages : [];
    return {
      order: Number(raw.order) || 0,
      type: convertPageType(String(raw.type || "Text")),
      id: Number(raw.id) || undefined,
      content: raw.content,
      assets: Array.isArray(raw.assets) ? (raw.assets as number[]) : [],
      creator: raw.creator,
      status: raw.status,
      parent_id: raw.parent_id,
      parent_page_id: raw.parent_page_id,
      sub_pages: nested.map((child) => transformPage(child as Record<string, unknown>)),
    };
  };

  const transformedPages = (card.pages ?? []).map((page) => transformPage(page));

  return (
    <CardPreviewModal
      // New card id → remount so page state always starts fresh at page 0.
      key={card.id}
      open={open}
      onClose={onClose}
      headerRightSlot={headerRightSlot}
      coverImageUrl={card.coverImageUrl}
      template={card.template}
      cardData={card}
      pages={transformedPages}
      additionalPages={additionalPages}
      showCoverPage={showCoverPage}
      onCollect={onCollect}
    />
  );
};

export default PublishedCardPreviewModal;
